import { lazy, Suspense } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { Spin } from 'antd';
import { BasicLayout } from '@/components/layout/BasicLayout';
import { RouteGuard } from '@/components/auth/RouteGuard';
import { ROUTE_DASHBOARDS } from '@/constants/routes';

const LoginPage = lazy(() => import('@/pages/auth/login'));
const UserListPage = lazy(() => import('@/pages/user/list'));
const DepartmentListPage = lazy(() => import('@/pages/department'));
const VehicleListPage = lazy(() => import('@/pages/vehicle/list'));
const VehicleGroupPage = lazy(() => import('@/pages/vehicle-group'));
const DriverListPage = lazy(() => import('@/pages/driver/list'));
const ApplicationListPage = lazy(() => import('@/pages/application/list'));
const DispatchPoolPage = lazy(() => import('@/pages/application/dispatch-pool'));
const DispatchListPage = lazy(() => import('@/pages/dispatch/list'));
const DispatchCreatePage = lazy(() => import('@/pages/dispatch/create'));
const DispatchDriverPage = lazy(() => import('@/pages/dispatch/driver'));
const OperationLogListPage = lazy(() => import('@/pages/operation-log/list'));
const LoginLogPage = lazy(() => import('@/pages/login-log'));
const AccountPage = lazy(() => import('@/pages/account'));
const FlowDefinitionListPage = lazy(() => import('@/pages/flow-definition/list'));
const FlowInstanceListPage = lazy(() => import('@/pages/flow-instance/list'));
const FlowHolidayListPage = lazy(() => import('@/pages/flow-holiday'));
const FlowQuotaPage = lazy(() => import('@/pages/flow-quota'));
const MyTodoPage = lazy(() => import('@/pages/my-todo'));
const MaintenanceOrderListPage = lazy(() => import('@/pages/maintenance-order/list'));
const MaintenanceRuleListPage = lazy(() => import('@/pages/maintenance-rule'));
const DictionaryListPage = lazy(() => import('@/pages/dictionary'));
const ReminderListPage = lazy(() => import('@/pages/reminder/list'));
const DriverScheduleListPage = lazy(() => import('@/pages/driver-schedule'));
const ShiftTemplateListPage = lazy(() => import('@/pages/shift-template'));
const PerformanceIndicatorListPage = lazy(() => import('@/pages/performance-indicator'));
const PerformanceRecordListPage = lazy(() => import('@/pages/performance-record'));
const RewardPenaltyListPage = lazy(() => import('@/pages/reward-penalty'));
const MySchedulePage = lazy(() => import('@/pages/my-schedule'));
const MyPerformancePage = lazy(() => import('@/pages/my-performance'));
const NotificationLogListPage = lazy(() => import('@/pages/notification-log/list'));
const NotificationChannelPage = lazy(() => import('@/pages/notification-channel'));
const NotificationInboxPage = lazy(() => import('@/pages/notification-inbox'));
const NotificationBlacklistPage = lazy(() => import('@/pages/notification-blacklist'));
const NotificationPreferencePage = lazy(() => import('@/pages/notification-preference'));
const NotificationEventPage = lazy(() => import('@/pages/notification-event'));
const NotificationTemplateListPage = lazy(() => import('@/pages/notification-template/list'));
const NotificationTemplateEditorPage = lazy(() => import('@/pages/notification-template/editor'));
const QuotaPoolListPage = lazy(() => import('@/pages/quota-pool'));
const CostRecordListPage = lazy(() => import('@/pages/cost-record'));
const ReportCenterPage = lazy(() => import('@/pages/report'));
const DispatchReportPage = lazy(() => import('@/pages/report/dispatch'));
const VehicleUtilizationReportPage = lazy(() => import('@/pages/report/vehicle-utilization'));
const DriverWorkloadReportPage = lazy(() => import('@/pages/report/driver-workload'));
const CostReportPage = lazy(() => import('@/pages/report/cost'));
const QuotaUsageReportPage = lazy(() => import('@/pages/report/quota-usage'));
const ApprovalEfficiencyReportPage = lazy(() => import('@/pages/report/approval-efficiency'));
const MaintenanceReportPage = lazy(() => import('@/pages/report/maintenance'));
const NotificationDeliveryReportPage = lazy(() => import('@/pages/report/notification-delivery'));
const ReportExportListPage = lazy(() => import('@/pages/report/exports'));
const ReportSubscriptionListPage = lazy(() => import('@/pages/report/subscriptions'));
const ReportMonitorPage = lazy(() => import('@/pages/report/monitor'));
const ReportDatasetPage = lazy(() => import('@/pages/report/dataset'));
const DispatchRecommendConfigPage = lazy(() => import('@/pages/dispatch/recommend-config'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const DashboardEditPage = lazy(() => import('@/pages/dashboard/edit'));
const DashboardTrendsPage = lazy(() => import('@/pages/dashboard/trends'));
const DashboardRankingsPage = lazy(() => import('@/pages/dashboard/rankings'));
const ForbiddenPage = lazy(() => import('@/pages/403'));
const NotFoundPage = lazy(() => import('@/pages/404'));

const fallback = (
  <div style={{ height: '100vh', display: 'grid', placeItems: 'center' }}>
    <Spin size="large" />
  </div>
);

const lazyEl = (el: React.ReactNode) => <Suspense fallback={fallback}>{el}</Suspense>;

/**
 * 路由实例。本文件刻意只导出非组件数据，避免与 React Fast Refresh
 * 不兼容导致 HMR invalidate（createBrowserRouter 实例化后路由表无法热替换）。
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: lazyEl(<LoginPage />),
  },
  {
    element: (
      <RouteGuard>
        <BasicLayout />
      </RouteGuard>
    ),
    children: [
      { index: true, element: <Navigate to={ROUTE_DASHBOARDS} replace /> },
      {
        path: 'dashboards',
        element: lazyEl(<DashboardPage />),
        handle: { permission: 'dashboard.view', title: '数据看板' },
      },
      {
        path: 'dashboards/edit',
        element: lazyEl(<DashboardEditPage />),
        handle: { permission: 'dashboard.config.self', title: '编辑看板' },
      },
      {
        path: 'dashboards/trends',
        element: lazyEl(<DashboardTrendsPage />),
        handle: { permission: 'dashboard.trend.view', title: '趋势分析' },
      },
      {
        path: 'dashboards/rankings',
        element: lazyEl(<DashboardRankingsPage />),
        handle: { permission: 'dashboard.view', title: '异常排行' },
      },
      {
        path: 'users',
        element: lazyEl(<UserListPage />),
        handle: { permission: 'user.list', title: '用户管理' },
      },
      {
        path: 'departments',
        element: lazyEl(<DepartmentListPage />),
        handle: { permission: 'department.list', title: '部门管理' },
      },
      {
        path: 'vehicles',
        element: lazyEl(<VehicleListPage />),
        handle: { permission: 'vehicle.list', title: '车辆管理' },
      },
      {
        path: 'vehicle-groups',
        element: lazyEl(<VehicleGroupPage />),
        handle: { permission: 'vehicle.list', title: '车辆分组' },
      },
      {
        path: 'drivers',
        element: lazyEl(<DriverListPage />),
        handle: { permission: 'driver.list', title: '司机管理' },
      },
      {
        path: 'applications',
        element: lazyEl(<ApplicationListPage />),
        handle: { permission: 'application.list', title: '用车申请' },
      },
      {
        path: 'applications/dispatch-pool',
        element: lazyEl(<DispatchPoolPage />),
        handle: { permission: 'application.dispatch_pool', title: '调度池' },
      },
      {
        path: 'dispatches',
        element: lazyEl(<DispatchListPage />),
        handle: { permission: 'dispatch.list', title: '派车单' },
      },
      {
        path: 'dispatches/new',
        element: lazyEl(<DispatchCreatePage />),
        handle: { permission: 'dispatch.create', title: '创建派车单' },
      },
      {
        path: 'driver/dispatches',
        element: lazyEl(<DispatchDriverPage />),
        handle: { permission: 'dispatch.driver_action', title: '我的派车（司机）' },
      },
      {
        path: 'dispatches/recommend-config',
        element: lazyEl(<DispatchRecommendConfigPage />),
        handle: { permission: 'dispatch.config', title: '推荐配置' },
      },
      {
        path: 'operation-logs',
        element: lazyEl(<OperationLogListPage />),
        handle: { permission: 'operation_log.list', title: '操作日志' },
      },
      {
        path: 'flow/definitions',
        element: lazyEl(<FlowDefinitionListPage />),
        handle: { permission: 'flow.definition.list', title: '流程定义' },
      },
      {
        path: 'flow/instances',
        element: lazyEl(<FlowInstanceListPage />),
        handle: { permission: 'flow.instance.list', title: '流程实例' },
      },
      {
        path: 'my-todo',
        element: lazyEl(<MyTodoPage />),
        handle: { title: '我的待办' },
      },
      {
        path: 'flow/holidays',
        element: lazyEl(<FlowHolidayListPage />),
        handle: { permission: 'flow.holiday.list', title: '节假日管理' },
      },
      {
        path: 'flow/quota',
        element: lazyEl(<FlowQuotaPage />),
        handle: { title: '紧急配额监控' },
      },
      {
        path: 'maintenance-orders',
        element: lazyEl(<MaintenanceOrderListPage />),
        handle: { permission: 'maintenance.list', title: '维护工单' },
      },
      {
        path: 'maintenance-rules',
        element: lazyEl(<MaintenanceRuleListPage />),
        handle: { permission: 'maintenance.rule.manage', title: '维护规则' },
      },
      {
        path: 'dictionaries',
        element: lazyEl(<DictionaryListPage />),
        handle: { permission: 'vehicle_type.manage', title: '字典管理' },
      },
      {
        path: 'reminders',
        element: lazyEl(<ReminderListPage />),
        handle: { permission: 'reminder.list', title: '到期提醒' },
      },
      {
        path: 'driver-schedules',
        element: lazyEl(<DriverScheduleListPage />),
        handle: { permission: 'schedule.list', title: '司机排班' },
      },
      {
        path: 'shift-templates',
        element: lazyEl(<ShiftTemplateListPage />),
        handle: { permission: 'schedule.template.manage', title: '班次模板' },
      },
      {
        path: 'performance-indicators',
        element: lazyEl(<PerformanceIndicatorListPage />),
        handle: { permission: 'performance.indicator.manage', title: '考核指标' },
      },
      {
        path: 'performance-records',
        element: lazyEl(<PerformanceRecordListPage />),
        handle: { permission: 'performance.record.list', title: '考核记录' },
      },
      {
        path: 'reward-penalties',
        element: lazyEl(<RewardPenaltyListPage />),
        handle: { permission: 'performance.reward.manage', title: '奖惩管理' },
      },
      {
        path: 'my-schedule',
        element: lazyEl(<MySchedulePage />),
        handle: { permission: 'driver.profile.read', title: '我的排班' },
      },
      {
        path: 'my-performance',
        element: lazyEl(<MyPerformancePage />),
        handle: { permission: 'driver.profile.read', title: '我的考核' },
      },
      {
        path: 'notification-logs',
        element: lazyEl(<NotificationLogListPage />),
        handle: { permission: 'notification.log.list', title: '发送日志' },
      },
      {
        path: 'notification-channels',
        element: lazyEl(<NotificationChannelPage />),
        handle: { permission: 'notification.channel.config', title: '通道配置' },
      },
      {
        path: 'messages',
        element: lazyEl(<NotificationInboxPage />),
        handle: { permission: 'notification.inbox.self', title: '我的消息' },
      },
      {
        path: 'notification-blacklist',
        element: lazyEl(<NotificationBlacklistPage />),
        handle: { permission: 'notification.blacklist.manage', title: '通知黑名单' },
      },
      {
        path: 'quota-pools',
        element: lazyEl(<QuotaPoolListPage />),
        handle: { permission: 'quota.pool.list', title: '额度池管理' },
      },
      {
        path: 'cost-records',
        element: lazyEl(<CostRecordListPage />),
        handle: { permission: 'cost.record.list', title: '费用工单' },
      },
      {
        path: 'my-notification-preference',
        element: lazyEl(<NotificationPreferencePage />),
        handle: { permission: 'notification.preference.self', title: '通知偏好' },
      },
      {
        path: 'notification-events',
        element: lazyEl(<NotificationEventPage />),
        handle: { permission: 'notification.template.list', title: '事件接线' },
      },
      {
        path: 'notification-templates',
        element: lazyEl(<NotificationTemplateListPage />),
        handle: { permission: 'notification.template.list', title: '消息模板' },
      },
      {
        path: 'notification-templates/new',
        element: lazyEl(<NotificationTemplateEditorPage />),
        handle: { permission: 'notification.template.create', title: '新建消息模板' },
      },
      {
        path: 'notification-templates/:id',
        element: lazyEl(<NotificationTemplateEditorPage />),
        handle: { permission: 'notification.template.detail', title: '编辑消息模板' },
      },
      {
        path: 'login-logs',
        element: lazyEl(<LoginLogPage />),
        handle: { permission: 'login_log.list', title: '登录日志' },
      },
      {
        path: 'account',
        element: lazyEl(<AccountPage />),
        handle: { title: '个人中心' },
      },
      // ============================================================
      // M13 报表中心
      // ============================================================
      {
        path: 'reports',
        element: lazyEl(<ReportCenterPage />),
        handle: { title: '报表中心' },
      },
      {
        path: 'reports/dispatch',
        element: lazyEl(<DispatchReportPage />),
        handle: { permission: 'report.dispatch.view', title: '派车统计' },
      },
      {
        path: 'reports/vehicle-utilization',
        element: lazyEl(<VehicleUtilizationReportPage />),
        handle: { permission: 'report.vehicle.view', title: '车辆利用率' },
      },
      {
        path: 'reports/driver-workload',
        element: lazyEl(<DriverWorkloadReportPage />),
        handle: { permission: 'report.driver.view', title: '司机工时' },
      },
      {
        path: 'reports/cost',
        element: lazyEl(<CostReportPage />),
        handle: { permission: 'report.cost.view', title: '费用统计' },
      },
      {
        path: 'reports/quota-usage',
        element: lazyEl(<QuotaUsageReportPage />),
        handle: { permission: 'report.quota.view', title: '额度使用率' },
      },
      {
        path: 'reports/approval-efficiency',
        element: lazyEl(<ApprovalEfficiencyReportPage />),
        handle: { permission: 'report.approval.view', title: '审批效率' },
      },
      {
        path: 'reports/maintenance',
        element: lazyEl(<MaintenanceReportPage />),
        handle: { permission: 'report.maintenance.view', title: '维护成本' },
      },
      {
        path: 'reports/notification-delivery',
        element: lazyEl(<NotificationDeliveryReportPage />),
        handle: { permission: 'report.notification.view', title: '通知投递' },
      },
      {
        path: 'reports/exports',
        element: lazyEl(<ReportExportListPage />),
        handle: { permission: ['report.export', 'report.export.task.manage'], title: '导出任务' }
      },
      {
        path: 'reports/subscriptions',
        element: lazyEl(<ReportSubscriptionListPage />),
        handle: { permission: ['report.subscription.self', 'report.subscription.admin'], title: '订阅管理' }
      },
      {
        path: 'reports/monitor',
        element: lazyEl(<ReportMonitorPage />),
        handle: { permission: ['report.dataset.refresh', 'report.dataset.backfill', 'report.export.task.manage'], title: '系统监控' }
      },
      {
        path: 'reports/dataset',
        element: lazyEl(<ReportDatasetPage />),
        handle: { permission: ['report.dataset.refresh', 'report.dataset.backfill'], title: '数据管理' }
      },
    ],
  },
  { path: '/403', element: lazyEl(<ForbiddenPage />) },
  { path: '*', element: lazyEl(<NotFoundPage />) },
]);
